import { AriaSelectOptions, HiddenSelect, useSelect } from '@react-aria/select';
import { useSelectState } from '@react-stately/select';
import React, { useEffect, useState } from 'react';
import { Button } from '../Button/Button';
import { cn } from '../cn';
import { Typography } from '../Typography/Typography';
import { PickerPopover } from './PickerPopover';
import { PickerListBox } from './PickerListbox';
import { IconChevronDown } from '../Icons';
import TextfieldHelperText from '../TextField/TextfieldHelperText';

type ISelectProps = {
  children: any,
  className?: string,
  buttonClassName?: string,
  isReadOnly?: boolean,
  position?: 'top' | 'bottom',
} & AriaSelectOptions<object>;

export const Picker = (props: ISelectProps) => {
  const correctedProps = {
    ...props,
    isInvalid: props.isInvalid || !!props.errorMessage,
  }
  const state = useSelectState(correctedProps);
  const ref = React.useRef(null);
  const {
    labelProps,
    triggerProps: baseTriggerProps,
    valueProps,
    menuProps,
    descriptionProps,
    errorMessageProps,
  } = useSelect(correctedProps, state, ref);
  const [buttonWidth, setButtonWidth] = useState(-1);
  const [isMounted, setIsMounted] = useState(false);

  const triggerProps = {
    ...baseTriggerProps,
    onPress: (e: any) => {
      // Prevent the default action to avoid closing the menu when clicking on the button
      e.preventDefault();
      // Toggle the menu open state
      state.setOpen(true);
    }
  }

  // Create an observer to watch the button width
  useEffect(() => {
    if(!ref.current) return;

    // Create a ResizeObserver to monitor the button's width
    const resizeObserver = new ResizeObserver((entries) => {
      for(const entry of entries) {
        const { width } = entry.contentRect;
        setButtonWidth(width + 28);
      }
    });

    // Start observing the button element
    resizeObserver.observe(ref.current);

    // Cleanup the observer when component unmounts
    return () => {
      resizeObserver.disconnect();
    };
  }, [ref.current]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if(!isMounted) {
    return null;
  }

  // create an observer to watch the button width inside of a useEffect
  // when the button width changes, update the menu width
  // when the menu width changes, update the button width
  // this will prevent the menu from being wider than the button
  // and the button from being wider than the menu
  return (
    <div className={cn(
      'flex flex-col flex-1',
      correctedProps.isDisabled && [
        'opacity-40',
      ],
      correctedProps.className
    )}>
      <Typography tag='label' size='sm' weight='bold' color='text-light' className={cn('flex flex-col')} {...labelProps}>
        <div>
          {correctedProps.label}
          {correctedProps.isRequired && (<span className='pl-0.5 text-text'>*</span>)}
        </div>

        <Button
          {...triggerProps}
          ref={ref}
          unstyled
          aria-label={''}
          aria-invalid={correctedProps.isInvalid}
          className={cn(
            'picker',
            'py-[5px] px-3',
            'bg-surface hover:bg-surface-dark',
            'text-text text-base font-medium leading-6',
            'border-1 border-border hover:border-border-hover disabled:border-border',
            'focus:ring-2',
            'flex justify-between gap-2 w-full',
            'items-center',
            correctedProps.isReadOnly && [
              'bg-surface-dark',
            ],
            correctedProps.errorMessage && [
              'border-error hover:border-error focus:border-error ',
            ],
            correctedProps.isDisabled && [
              'disabled:bg-surface',
              'cursor-not-allowed',
            ],
            correctedProps.buttonClassName,
          )}
        >
          <div {...valueProps} className='line-clamp-1 text-left'>
            {state.selectedItem
              ? state.selectedItem.rendered
              : correctedProps.placeholder ? correctedProps.placeholder : 'Select an option'}
          </div>
          <div
            aria-hidden='true'
            className='size-[22px] flex items-center justify-center'
          >
            <IconChevronDown className={cn('size-[22px]', state.isOpen ? 'rotate-180' : '')}/>
          </div>
        </Button>
      </Typography>
      {state.isOpen && (
        <PickerPopover width={buttonWidth} state={state} triggerRef={ref} placement={`${correctedProps.position || 'top'} start`}>
          <PickerListBox
            {...menuProps}
            state={state}
          />
        </PickerPopover>
      )}
      <HiddenSelect
        isDisabled={correctedProps.isDisabled}
        state={state}
        triggerRef={ref}
        label={correctedProps.label}
        name={correctedProps.name}
      />
      <div {
        ...((correctedProps.description && !correctedProps.errorMessage) ? descriptionProps
          : correctedProps.errorMessage ? errorMessageProps : '')
      }>
        {(correctedProps.errorMessage || correctedProps.description) ? (
          <TextfieldHelperText status={!correctedProps.errorMessage ? 'neutral' : 'error'}>
            {correctedProps.errorMessage instanceof Function ? correctedProps.errorMessage(null as any) : correctedProps.errorMessage || correctedProps.description}
          </TextfieldHelperText>
        ) : null}
      </div>
    </div>
  );
};
